/// <amd-module name="Build/Queue-Panel/VariablePanel" />
import * as React from "react";
import { IVariableData, IDictionaryStringTo } from "Types";

import { FormInputLabel } from "Build/Queue-Panel/FormInputLabel";
import * as Resources from "Build/Queue-Panel/Resources";

import { Checkbox } from "VSSUI/Checkbox";
import { FormItem } from "VSSUI/FormItem";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { TextField } from "VSSUI/TextField";

import { format } from "VSS/Core/Util/String";
import { IObservableArray } from "VSS/Core/Observable";

export interface IVariablePanelState {
    variable: IVariableData;
}

export interface IVariablePanelProps {
    variable: IVariableData;
    isNewVariable: boolean; // if the panel is used for creating a new variable or updating an existing one
    runtimeVariables: IObservableArray<IVariableData>;
    onVariableAdded?(variable: IVariableData): void;
    onVariableUpdated?(variable: IVariableData): void;
    onDismiss(): void;
}

export class VariablePanel extends React.Component<IVariablePanelProps, IVariablePanelState> {
    private _originalVariableValue: string | undefined;
    constructor(props: IVariablePanelProps) {
        super(props);

        this.state = {
            variable: this.props.variable
        };
        this._originalVariableValue = !this.props.isNewVariable ? this.props.variable.value : undefined;
    }

    public render(): JSX.Element {
        const title = this.props.isNewVariable ? Resources.AddVariablePanelTitle : Resources.UpdateVariablePanelTitle;
        const primaryButtonText = this.props.isNewVariable ? Resources.CreateButtonText : Resources.UpdateButtonText;
        const errorMessage = this._getDuplicateValidationMessage();
        const invalidVariable = !this._isValidVariableName() || errorMessage !== "" ;
        const defaultActiveElement = this.props.isNewVariable ? ".variable-panel-variable-name-input" : ".variable-panel-variable-value-input";

        return (
            <Panel
                backButtonProps={{
                    subtle: true,
                    iconProps: { iconName: "Back" },
                    ariaLabel: Resources.Back,
                    onClick: this.props.onDismiss
                }}
                onDismiss={this.props.onDismiss}
                escDismiss={true}
                titleProps={{ text: title }}
                className="variable-panel"
                defaultActiveElement={defaultActiveElement}
                footerButtonProps={[
                {
                    text: Resources.CancelButtonText,
                    onClick: this.props.onDismiss
                },
                {
                    text: primaryButtonText, 
                    primary: true, 
                    onClick: this._clickOk,
                    disabled: invalidVariable || this._isUnchangedVariableValue()
                }]}
            >
                <VariablePanelContent
                    variable={this.props.variable}
                    isNewVariable={this.props.isNewVariable}
                    validationMessage={errorMessage}
                    onNameChanged={this._onNameChanged}
                    onValueChanged={this._onValueChanged}
                    onKeyDown={this._onKeyDown}
                />
            </Panel>
        );
    }

    private _onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        // If we press enter from within the panel this will click the create button.
        const errorMessage = this._getDuplicateValidationMessage();
        const invalidVariable = !this._isValidVariableName() || errorMessage !== "" ;
        if (event.key === "Enter" && !event.defaultPrevented && !invalidVariable) {
            this._clickOk();
            event.preventDefault();
        }
    }

    private _isValidVariableName = (): boolean => {
        const itemName = this.state.variable.name;
        return !!itemName && itemName.trim() !== "";
    }

    private _isUnchangedVariableValue = (): boolean => {
        return !this.props.isNewVariable && this._originalVariableValue === this.state.variable.value;
    }

    private _getDuplicateValidationMessage = (): string => {
        const item = this.state.variable
        const itemName = item.name ? item.name.trim().toLocaleLowerCase() : "";
        const isDuplicate = this._getDuplicateVariableNamesMap()[itemName] > 1;
        return isDuplicate ? format(Resources.DuplicateVariableMessage, item.name) : "";
    }

    private _getDuplicateVariableNamesMap = (): IDictionaryStringTo<number> => {
        let duplicateVariableNamesMap: IDictionaryStringTo<number> = {};

        if (!this.props.runtimeVariables) {
            return duplicateVariableNamesMap;
        }

        // add the new variable if we are creating a new var before checking for dups
        const updatedVariables = this.props.isNewVariable ? this.props.runtimeVariables.value.concat([this.state.variable]) : this.props.runtimeVariables.value;

        updatedVariables.forEach((variable: IVariableData) => {
            if (variable && variable.name) {
                let variableName: string = variable.name ?  variable.name.trim().toLocaleLowerCase() : "";

                if (!duplicateVariableNamesMap.hasOwnProperty(variableName)) {
                    duplicateVariableNamesMap[variableName] = 1;
                } else {
                    duplicateVariableNamesMap[variableName]++;
                }
            } 
        });

        return duplicateVariableNamesMap;
    }

    private _clickOk = () => {
        if (this.props.isNewVariable && this.props.onVariableAdded)
        {
            this.props.onVariableAdded(this.state.variable);
        }
        else if (!this.props.isNewVariable && this.props.onVariableUpdated)
        {
            this.props.onVariableUpdated(this.state.variable);
        }

        this.props.onDismiss();
    }

    private _onNameChanged = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> , newValue: string) => {
        this.state.variable.name = newValue;
        this.setState({
            variable: this.state.variable
        });
    }

    private _onValueChanged = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> , newValue: string) => {
        this.state.variable.value = newValue;
        this.setState({
            variable: this.state.variable
        });
    }
}

interface IVariablePanelContentProps {
    variable: IVariableData;
    isNewVariable: boolean; // if the panel is used for creating a new variable or updating an existing one
    validationMessage: string;
    onNameChanged(ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> , newValue: string): void;
    onValueChanged(ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> , newValue: string): void;
    onKeyDown(ev: React.KeyboardEvent<HTMLDivElement>): void;
}

class VariablePanelContent extends React.Component<IVariablePanelContentProps> {
    private _valueTextFieldRef: TextField;
    private _nameTextFieldRef: TextField;

    constructor(props: IVariablePanelContentProps) {
        super(props);

        this.state = {
            variable: this.props.variable
        };
    }

    public render(): JSX.Element {
        return (
            <div className="variable-panel-content flex-grow" onKeyDown={this.props.onKeyDown}>
                {this._getVariableNameControl()}
                {this._getVariableValueControl()}
                {!this.props.isNewVariable && this.props.variable && this.props.variable.isSecret && this._getVariableSecretControl()}
            </div>
        );
    }

    public componentDidMount() {
        if (this.props.isNewVariable)
        {
            this._nameTextFieldRef && this._nameTextFieldRef.focus();
            this._nameTextFieldRef && this._nameTextFieldRef.select();
        }
        else {
            this._valueTextFieldRef && this._valueTextFieldRef.focus();
            this._valueTextFieldRef && this._valueTextFieldRef.select();
        }
    }

    private _getVariableNameControl = (): JSX.Element => {
        return  (
            <FormItem 
                className="flex-grow margin-bottom-16"
                label={<FormInputLabel primaryLabel={Resources.NameFieldLabel} secondaryLabel={Resources.RequiredLabel} />}
                error={this.props.validationMessage !== ""}
                message={this.props.validationMessage}
            >
                <TextField
                    ariaLabel={Resources.NameFieldLabel}
                    ref={element => {this._nameTextFieldRef = element!}}
                    value={this.props.variable.name}
                    inputClassName="variable-panel-variable-name-input"
                    onChange={this.props.onNameChanged}
                    disabled={this.props.variable.disableDelete}
                    required={true}
                />
            </FormItem>
        );
    }

    private _getVariableValueControl = (): JSX.Element => {
        const variable = this.props.variable;
        const inputType = variable.isSecret ? "password" : "text";
        
        // add some filler text if secret value is null
        const value = variable.isSecret && variable.value === null ? this.c_secretVariableFillerText : variable.value

        return (
            <FormItem 
                label={Resources.ValueFieldLabel}
                className="flex-grow margin-bottom-8"
            >
                <TextField 
                    value={value} 
                    inputClassName="variable-panel-variable-value-input"
                    ref={element =>{this._valueTextFieldRef = element!}}
                    inputType={inputType} 
                    onChange={this.props.onValueChanged}
                />
            </FormItem>
        );
    }

    private _getVariableSecretControl = (): JSX.Element => {
        const variableIsSecret = this.props.variable ? this.props.variable.isSecret : false;

        return (
            <Checkbox
                className="no-h-padding"
                label={Resources.SecretFieldLabel} 
                checked={variableIsSecret} 
                disabled={true}
            />
        );
    }

    private c_secretVariableFillerText = "*****";
}